import { getLatLng } from '@/components/blocks/AddressForm/utils';
import { EMPTY_STRING } from '@/data/constants/order';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { fetcher } from '@/data/Content/_StoreLocator';
import { geoCodeByLatLng } from '@/data/Content/GeoCodeByLatLng';
import { DEFAULT_COUNTRY_ID, geocodeByZipCode } from '@/data/Content/GeocodeByZipCode';
import { useLocationByZipCode } from '@/data/Content/HdmLocationByZipCode';
import stateCitiesData from '@/data/Content/StoreLocator/storeLocatorStateCitiesList.json';
import { useSettings } from '@/data/Settings';
import { Address } from '@/data/types/Address';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useStoreSwitcher } from '@/data/utils/useStoreSwitcher';
import { findStoreDataByStLocId, findStoreIdByZipCode } from '@/utils/storeLocatorUtils';
import { saveDeliveryMarket } from '@/utils/useDeliveryMarket';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import {
	ADDRESS_COMPONENT_TYPES,
	DEFAULT_DELIVERY_LOCALITY,
	DEFAULT_DELIVERY_STATE,
	DEFAULT_DELIVERY_ZIPCODE,
	DEFAULT_STATE_ID,
	DEFAULT_STORE_ID,
	MAX_EXP_TIME,
	MIN_EXP_TIME,
	STORE_LOCATOR_RADIUS,
} from 'shared/custom/constants/commonConstants';
import {
	buildDeliveryLocationCookie,
	buildStoreCookie,
	getDeliveryLocationCookie,
	getStoreIdAndStateId,
	hasCustomerDeliveryLocation,
} from 'shared/custom/utils/cookieHandler';

const MIN_ZIPCODE_LENGTH = 5;

type Props = {
	zipCodeInputValue?: string;
	setZipCodeInputValue?: Dispatch<SetStateAction<string>>;
	handleClose?: () => void;
};

export const useZipCodeInput = ({
	zipCodeInputValue,
	setZipCodeInputValue,
	handleClose,
}: Props) => {
	const statesData = stateCitiesData.formScheduleObject;
	const storesList = useMemo(
		() => statesData?.flatMap((stores) => stores.stores) ?? [],
		[statesData]
	);
	const { switchStore } = useStoreSwitcher(storesList);

	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId } = getClientSideCommon(settings, router);

	const { getLocationByZipCode } = useLocationByZipCode();
	const trackException = useTrackException();

	const [focused, setFocused] = useState(false);
	const [isZipCodeValid, setIsZipCodeValid] = useState(false);
	const [loading, setLoading] = useState(false);
	const [zipCodeError, setZipCodeError] = useState(false);

	const handleOnBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const zipCodeValue = e.target.value;
		if (!zipCodeValue) {
			setFocused(false);
		}
		setZipCodeError(zipCodeValue.length > 0 && zipCodeValue.length < MIN_ZIPCODE_LENGTH);
	};

	const buildDefaultDeliveryLocationCookie = () => {
		const zipCode = DEFAULT_DELIVERY_ZIPCODE;
		const locality = DEFAULT_DELIVERY_LOCALITY;
		const state = DEFAULT_DELIVERY_STATE;
		buildDeliveryLocationCookie({ zipCode, locality, state }, MAX_EXP_TIME);
	};

	const handleZipCodeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const zipCodeValue = event.target.value;
		setZipCodeInputValue?.(zipCodeValue);
		setFocused(true);

		setZipCodeError(false);

		if (zipCodeValue.length >= MIN_ZIPCODE_LENGTH) {
			setIsZipCodeValid(true);
			return;
		}

		setIsZipCodeValid(false);
	};

	const handleUseCurrentLocation = () => {
		const onGetCurrentPosition = async (geolocation: GeolocationPosition) => {
			try {
				setLoading(true);
				const { results, status } = await geoCodeByLatLng({
					lat: geolocation.coords.latitude,
					lng: geolocation.coords.longitude,
				});

				if (status !== google.maps.GeocoderStatus.OK) {
					buildDefaultDeliveryLocationCookie();
					handleSetDefaultStore();
					return;
				}

				buildDeliveryLocationCookieFromGeoCode(results);
				updateSelectedStore(geolocation);
			} catch (e) {
				buildDefaultDeliveryLocationCookie();
				handleSetDefaultStore();
				trackException(e);
			} finally {
				setLoading(false);
				handleClose?.();
			}
		};

		navigator.geolocation.getCurrentPosition(onGetCurrentPosition, () => {
			if (!hasCustomerDeliveryLocation()) buildDefaultDeliveryLocationCookie();
		});
	};

	const handleSetDefaultStore = async () => {
		const geoLocationCookie = getStoreIdAndStateId();
		buildStoreCookie(geoLocationCookie, true, MIN_EXP_TIME);
		await switchStore(DEFAULT_STATE_ID, DEFAULT_STORE_ID, '', true);
	};

	const updateSelectedStore = (
		geolocation: GeolocationPosition,
		zipCode = '',
		isZipCodeCoords = false
	) => {
		const { latitude: lat, longitude: lng } = geolocation.coords;
		const deliveryLocation = getDeliveryLocationCookie();
		const marketZipCode =
			zipCode || (deliveryLocation?.setByCustomer ? deliveryLocation.zipCode : '');
		const coords = isZipCodeCoords ? { lat, lng } : {};
		fetcher(true)(
			storeId,
			`${geolocation.coords.latitude}`,
			`${geolocation.coords.longitude}`,
			{ radius: STORE_LOCATOR_RADIUS },
			{}
		)
			.then((response) => response)
			.then(async (res) => {
				const geoStore = res?.PhysicalStore;
				let store = geoStore?.[0];
				if (zipCode) {
					const found = geoStore?.find((store) => store.postalCode?.trim() === zipCode);
					store = found ?? geoStore?.[0];
				}

				if (store?.storeName) {
					marketZipCode &&
						saveDeliveryMarket({ zipCode: marketZipCode, stLocId: store.storeName, ...coords });
					await switchStore('', store.storeName ?? '', '', true);
				} else {
					marketZipCode &&
						saveDeliveryMarket({ zipCode: marketZipCode, ...coords, failure: 'noStoreInRadius' });
					handleSetDefaultStore();
				}
			})
			.catch((e) => {
				marketZipCode && saveDeliveryMarket({ zipCode: marketZipCode });
				handleSetDefaultStore();
				trackException(e);
			});
	};

	const buildDeliveryLocationCookieFromZipCode = async (
		zipCode: string,
		geocodeResults?: google.maps.GeocoderResult[] | null
	) => {
		const result = await getLocationByZipCode(zipCode);

		if (!result) {
			const components = geocodeResults?.[0]?.address_components;
			const findComponent = (type: string) =>
				components?.find((c: google.maps.GeocoderAddressComponent) => c.types.includes(type));
			const fallbackStore = findStoreByZipCode(zipCode);

			buildDeliveryLocationCookie(
				{
					zipCode,
					locality:
						findComponent(ADDRESS_COMPONENT_TYPES.LOCALITY)?.short_name ??
						fallbackStore?.city ??
						DEFAULT_DELIVERY_LOCALITY,
					state:
						findComponent(ADDRESS_COMPONENT_TYPES.ADMIN_AREA_LEVEL_1)?.long_name ??
						fallbackStore?.stLocName ??
						DEFAULT_DELIVERY_STATE,
					setByCustomer: true,
				},
				MAX_EXP_TIME
			);
			return;
		}

		// A colony (neighborhood) is more precise than the city when the zip code has one.
		const locality = (result?.colonies?.[0]?.name ?? result?.city?.[0]?.name)?.toLowerCase();
		const state = result?.state?.name.toLowerCase();

		buildDeliveryLocationCookie({ zipCode, locality, state, setByCustomer: true }, MAX_EXP_TIME);
	};

	const findStoreByZipCode = (zipCode?: string) => {
		const fallbackStoreId = zipCode && findStoreIdByZipCode(zipCode);
		const stateData = fallbackStoreId && findStoreDataByStLocId(fallbackStoreId);
		if (!stateData) return undefined;
		return stateData.stores.find((store) => store.stLocId === fallbackStoreId);
	};

	const buildDeliveryLocationCookieFromGeoCode = (
		results: google.maps.GeocoderResult[] | null,
		zipCode?: string
	) => {
		if (!results || results.length === 0) {
			buildDefaultDeliveryLocationCookie();
			return;
		}

		const stateCity = results[0].address_components;
		const findComponent = (type: string) =>
			stateCity.find((c: google.maps.GeocoderAddressComponent) => c.types.includes(type));

		const foundZipCode = findComponent(ADDRESS_COMPONENT_TYPES.POSTAL_CODE)?.short_name;
		const foundLocality = findComponent(ADDRESS_COMPONENT_TYPES.LOCALITY)?.short_name;
		const foundState = findComponent(ADDRESS_COMPONENT_TYPES.ADMIN_AREA_LEVEL_1)?.long_name;
		const fallbackStore = findStoreByZipCode(foundZipCode ?? zipCode);

		buildDeliveryLocationCookie(
			{
				zipCode: foundZipCode ?? fallbackStore?.zipcode ?? DEFAULT_DELIVERY_ZIPCODE,
				locality: foundLocality ?? fallbackStore?.city ?? DEFAULT_DELIVERY_LOCALITY,
				state: foundState ?? fallbackStore?.stLocName ?? DEFAULT_DELIVERY_STATE,
				setByCustomer: true,
			},
			MAX_EXP_TIME
		);
	};

	const updateStoreByCoords = (zipCode: string, results: google.maps.GeocoderResult[] | null) => {
		const latLng = getLatLng(results);
		if (latLng) {
			const [latStr, lngStr] = latLng.split(',');
			const latitude = Number.parseFloat(latStr);
			const longitude = Number.parseFloat(lngStr);
			updateSelectedStore(
				{ coords: { latitude, longitude } } as GeolocationPosition,
				zipCode,
				true
			);
		} else {
			saveDeliveryMarket({ zipCode });
		}
	};

	const updateStoreWithLocalData = (zipCode: string, addressId?: string) => {
		const fallbackStoreId = findStoreIdByZipCode(zipCode);
		const stateData = fallbackStoreId && findStoreDataByStLocId(fallbackStoreId);

		if (!fallbackStoreId || stateData === '' || !stateData) {
			setZipCodeError(true);
			return false;
		}

		const store = stateData.stores.find((store) => store.stLocId === fallbackStoreId);
		const locality = store?.shortName || DEFAULT_DELIVERY_LOCALITY;
		const state = store?.stLocName || DEFAULT_DELIVERY_STATE;
		store &&
			updateSelectedStore(
				{
					coords: {
						latitude: Number.parseFloat(store.latitude),
						longitude: Number.parseFloat(store.longitude),
					},
				} as GeolocationPosition,
				zipCode
			);
		buildDeliveryLocationCookie(
			{ addressId, zipCode, locality, state, setByCustomer: true },
			MAX_EXP_TIME
		);
		return true;
	};

	const resetZipCodeForm = () => {
		setZipCodeInputValue?.('');
		setIsZipCodeValid(false);
		setFocused(false);
	};

	const handleSubmitZipCode = async (zipCode: string) => {
		try {
			setLoading(true);
			const { results, status } = await geocodeByZipCode(zipCode, undefined, DEFAULT_COUNTRY_ID);

			if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
				const isStoreResolved = updateStoreWithLocalData(zipCode);
				// Keep the drawer open and surface the input error when the zip code
				// can't be resolved to a store instead of closing the panel.
				if (!isStoreResolved) {
					return;
				}
				// The store was already resolved locally above; only refine the
				// displayed locality/state here, without switching to the default store.
				buildDeliveryLocationCookieFromZipCode(zipCode);
				resetZipCodeForm();
				handleClose?.();
				return;
			}

			if (status === google.maps.GeocoderStatus.OK) {
				buildDeliveryLocationCookieFromZipCode(zipCode, results);
				updateStoreByCoords(zipCode, results);
			} else {
				buildDeliveryLocationCookieFromZipCode(zipCode);
				saveDeliveryMarket({ zipCode });
				handleSetDefaultStore();
			}
			resetZipCodeForm();
			handleClose?.();
		} catch (e) {
			buildDefaultDeliveryLocationCookie();
			handleSetDefaultStore();
			resetZipCodeForm();
			handleClose?.();
			trackException(e);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectAddress = async (address: Address) => {
		const { addressId, city: locality, state, zipCode } = address;
		let geocode: Awaited<ReturnType<typeof geocodeByZipCode>> | undefined;
		try {
			geocode = await geocodeByZipCode(zipCode, undefined, DEFAULT_COUNTRY_ID);
		} catch (e) {
			trackException(e);
		}

		if (geocode?.status === google.maps.GeocoderStatus.ZERO_RESULTS) {
			const isStoreResolved = updateStoreWithLocalData(zipCode, addressId);
			if (isStoreResolved) return;
			resetZipCodeError();
		}

		buildDeliveryLocationCookie(
			{ addressId, zipCode, locality, state, setByCustomer: true },
			MAX_EXP_TIME
		);

		if (geocode?.status === google.maps.GeocoderStatus.OK) {
			updateStoreByCoords(zipCode, geocode.results);
		} else {
			saveDeliveryMarket({ zipCode });
			handleSetDefaultStore();
		}
	};

	const resetZipCodeError = () => {
		setZipCodeError(false);
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (zipCodeInputValue === EMPTY_STRING) {
			setZipCodeError(true);
			return;
		}
		if (isZipCodeValid) {
			handleSubmitZipCode(zipCodeInputValue ?? EMPTY_STRING);
		}
	};

	return {
		focused,
		handleOnBlur,
		handleSubmit,
		buildDeliveryLocationCookieFromZipCode,
		handleSelectAddress,
		handleUseCurrentLocation,
		handleZipCodeInputChange,
		isZipCodeValid,
		loading,
		setFocused,
		zipCodeError,
		resetZipCodeError,
	};
};
